// Copyright (c) 2024-2026 Echonum LLC. All rights reserved.
// Product: Exploit Score ETS (Exploit Threat Score)
// Version: 2.0.4
//
// This file is part of Exploit Score ETS, proprietary software developed by
// Echonum LLC. Unauthorized copying, distribution, modification, or use of
// this file, via any medium, is strictly prohibited without express written
// permission from Echonum LLC.
//
// Confidential and Proprietary.

import { useEffect, useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import api from '../api/client'
import toast from 'react-hot-toast'
import { confirmDialog } from '../components/DialogHost'
import {
  CreditCard,
  CheckCircle,
  AlertCircle,
  Download,
  Plus,
  Trash2,
  Loader2,
  X,
  Check,
  RefreshCw
} from 'lucide-react'

// Initialize Stripe - will be set after fetching config
let stripePromise: Promise<any> | null = null

const getStripePromise = async () => {
  if (!stripePromise) {
    try {
      const res = await api.get('/api/portal/billing/config')
      if (res.data.stripe_publishable_key) {
        stripePromise = loadStripe(res.data.stripe_publishable_key)
      }
    } catch (error) {
      console.error('Failed to load Stripe config:', error)
    }
  }
  return stripePromise
}

interface Subscription {
  id: string
  plan_id: string
  plan_name: string
  billing_cycle: string
  status: string
  current_period_end: string
  cancel_at_period_end: boolean
  license_key?: string
}

interface PaymentMethod {
  id: string
  card_brand: string
  card_last4: string
  card_exp_month: number
  card_exp_year: number
  is_default: boolean
}

interface Invoice {
  id: string
  amount_due: number
  amount_paid: number
  currency: string
  status: string
  period_start: string
  period_end: string
  invoice_pdf_url?: string
  created_at: string
}

interface Plan {
  id: string
  name: string
  price_monthly: number
  price_annual: number
  max_endpoints: number
  max_users: number
  max_vulnerabilities: number
  features: string[]
}

const PLAN_FEATURES: Record<string, string[]> = {
  standard: ['Advanced scoring', 'Email support', 'Standard integrations', 'Basic dashboards', '1,000 endpoints', '10 users'],
  professional: ['Full scoring', 'Priority support', 'All integrations', 'Custom dashboards', 'API access', '5,000 endpoints', '25 users'],
  enterprise: ['Custom ML models', 'Dedicated support', 'SSO/SAML', 'SLA guarantee', '25,000 endpoints', '100 users'],
  unlimited: ['Everything in Enterprise', 'Unlimited endpoints', 'Unlimited users', 'On-premise option', 'White-label', '24/7 support'],
}

// Helper to truncate license key for display
const truncateLicenseKey = (key: string, showChars: number = 8): string => {
  if (!key || key.length <= showChars * 2 + 3) return key
  return `${key.slice(0, showChars)}...${key.slice(-showChars)}`
}

// Add Card Form Component
function AddCardForm({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) {
  const stripe = useStripe()
  const elements = useElements()
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return

    setIsProcessing(true)
    setError(null)

    try {
      // Get setup intent from backend
      const setupRes = await api.post('/api/portal/billing/setup-intent')
      const clientSecret = setupRes.data.client_secret

      // Confirm card setup
      const cardElement = elements.getElement(CardElement)
      if (!cardElement) {
        throw new Error('Card element not found')
      }

      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      })

      if (stripeError) {
        setError(stripeError.message || 'Failed to add card')
      } else if (setupIntent?.status === 'succeeded') {
        // Save the payment method to our database
        try {
          await api.post('/api/portal/billing/confirm-setup', {
            payment_method_id: setupIntent.payment_method
          })
          toast.success('Card added successfully!')
          onSuccess()
        } catch (saveErr: any) {
          console.error('Failed to save payment method:', saveErr)
          const errorMsg = saveErr.response?.data?.detail || 'Failed to save card to account'
          setError(errorMsg)
          toast.error(errorMsg)
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to add card')
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 bg-gray-800 rounded-lg">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#fff',
                '::placeholder': { color: '#6b7280' },
              },
              invalid: { color: '#ef4444' },
            },
          }}
        />
      </div>
      {error && <p className="text-red-500 text-sm">{error}</p>}
      <div className="flex gap-3 justify-end">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
        <button type="submit" disabled={!stripe || isProcessing} className="btn btn-primary">
          {isProcessing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              Add Card
            </>
          )}
        </button>
      </div>
    </form>
  )
}

// Main Billing Component
export default function Billing() {
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [plans, setPlans] = useState<Plan[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [billingEnabled, setBillingEnabled] = useState(false)
  const [stripeLoaded, setStripeLoaded] = useState(false)
  const [stripeInstance, setStripeInstance] = useState<any>(null)

  // Checkout state
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly')
  const [isCheckingOut, setIsCheckingOut] = useState(false)
  const [promoCode, setPromoCode] = useState('')
  const [promoValidation, setPromoValidation] = useState<{ valid: boolean; message: string } | null>(null)

  // Add card modal
  const [showAddCard, setShowAddCard] = useState(false)

  // Show plans for upgrade
  const [showPlans, setShowPlans] = useState(false)

  // Invoice sync
  const [isSyncingInvoices, setIsSyncingInvoices] = useState(false)

  useEffect(() => {
    loadBillingData()
    initStripe()
  }, [])

  const initStripe = async () => {
    const stripe = await getStripePromise()
    if (stripe) {
      setStripeInstance(stripe)
      setStripeLoaded(true)
    }
  }

  const loadBillingData = async () => {
    try {
      const [configRes, subRes, pmRes, invRes, plansRes] = await Promise.all([
        api.get('/api/portal/billing/config'),
        api.get('/api/portal/billing/subscription'),
        api.get('/api/portal/billing/payment-methods'),
        api.get('/api/portal/billing/invoices'),
        api.get('/api/portal/billing/plans')
      ])

      setBillingEnabled(configRes.data.billing_enabled)
      setSubscription(subRes.data)
      setPaymentMethods(pmRes.data)
      setInvoices(invRes.data)
      setPlans(plansRes.data)
    } catch (error) {
      console.error('Failed to load billing:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCheckout = async (planId: string) => {
    setIsCheckingOut(true)
    try {
      const res = await api.post('/api/portal/billing/checkout-session', {
        plan_id: planId,
        billing_cycle: billingCycle,
        success_url: `${window.location.origin}/billing?success=true`,
        cancel_url: `${window.location.origin}/billing?cancelled=true`,
        promo_code: promoCode || undefined
      })

      // Redirect to Stripe Checkout
      if (res.data.url) {
        window.location.href = res.data.url
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout')
    } finally {
      setIsCheckingOut(false)
    }
  }

  const handleValidatePromo = async () => {
    if (!promoCode.trim()) return

    try {
      const res = await api.post('/api/portal/billing/promo-code/validate', { code: promoCode })
      setPromoValidation({ valid: res.data.valid, message: res.data.message })
    } catch (error) {
      setPromoValidation({ valid: false, message: 'Failed to validate code' })
    }
  }

  const handleCancelSubscription = async () => {
    if (!(await confirmDialog({
      title: 'Cancel subscription',
      message: 'Are you sure you want to cancel your subscription? It will remain active until the end of the billing period.',
      confirmText: 'Cancel subscription',
      cancelText: 'Keep subscription',
      danger: true,
    }))) {
      return
    }

    try {
      await api.delete('/api/portal/billing/subscription')
      toast.success('Subscription will be cancelled at the end of the billing period')
      loadBillingData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription')
    }
  }

  const handleReactivate = async () => {
    try {
      await api.post('/api/portal/billing/subscription/reactivate')
      toast.success('Subscription reactivated!')
      loadBillingData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate')
    }
  }

  const handleDeletePaymentMethod = async (id: string) => {
    if (!(await confirmDialog({ title: 'Remove payment method', message: 'Remove this payment method?', confirmText: 'Remove', danger: true }))) return

    try {
      await api.delete(`/api/portal/billing/payment-methods/${id}`)
      toast.success('Payment method removed')
      loadBillingData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to remove payment method')
    }
  }

  const handleSetDefaultPaymentMethod = async (id: string) => {
    try {
      await api.post(`/api/portal/billing/payment-methods/${id}/default`)
      toast.success('Default payment method updated')
      loadBillingData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to set default')
    }
  }

  const handleSyncInvoices = async () => {
    setIsSyncingInvoices(true)
    try {
      const res = await api.post('/api/portal/billing/invoices/sync')
      toast.success(res.data.message || 'Invoices synced')
      loadBillingData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to sync invoices')
    } finally {
      setIsSyncingInvoices(false)
    }
  }

  // Check for success/cancel URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('success') === 'true') {
      toast.success('Subscription activated successfully!')
      window.history.replaceState({}, '', '/billing')
      loadBillingData()
    } else if (params.get('cancelled') === 'true') {
      toast.error('Checkout was cancelled')
      window.history.replaceState({}, '', '/billing')
    }
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    )
  }

  if (!billingEnabled) {
    return (
      <div className="space-y-6 fade-in">
        <h1 className="text-2xl font-bold">Billing & Subscription</h1>
        <div className="card text-center py-12">
          <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Billing Not Configured</h2>
          <p className="text-gray-400">Please contact support to set up your subscription.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 fade-in">
      <h1 className="text-2xl font-bold">Billing & Subscription</h1>

      {/* Current Subscription */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Current Plan</h2>
        {subscription ? (
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold">{subscription.plan_name}</span>
                <span className={`badge ${subscription.status === 'active' ? 'badge-success' : 'badge-warning'}`}>
                  {subscription.status}
                </span>
              </div>
              <p className="text-gray-400 mt-1">
                Billed {subscription.billing_cycle} &middot;
                {subscription.cancel_at_period_end ? ' Cancels' : ' Renews'} on {new Date(subscription.current_period_end).toLocaleDateString()}
              </p>
              {subscription.license_key && (
                <div className="mt-3 p-3 bg-gray-800 rounded-lg">
                  <p className="text-xs text-gray-400 mb-1">License Key</p>
                  <code className="text-sm text-primary-500" title={subscription.license_key}>
                    {truncateLicenseKey(subscription.license_key)}
                  </code>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowPlans(!showPlans)}
                className="btn btn-secondary"
              >
                {showPlans ? 'Hide Plans' : 'Upgrade Plan'}
              </button>
              {subscription.cancel_at_period_end ? (
                <button onClick={handleReactivate} className="btn btn-primary">
                  Reactivate
                </button>
              ) : (
                <button onClick={handleCancelSubscription} className="btn btn-danger">
                  Cancel Plan
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Active Subscription</h3>
            <p className="text-gray-400 mb-4">Choose a plan below to get started</p>
          </div>
        )}
      </div>

      {/* Plans */}
      {(showPlans || !subscription) && plans.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold">{subscription ? 'Change Plan' : 'Choose a Plan'}</h2>
            {/* Promo Code */}
            <div className="flex gap-3 items-center">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value.toUpperCase())
                  setPromoValidation(null)
                }}
                placeholder="Promo Code"
                className="w-32 text-sm"
              />
              <button onClick={handleValidatePromo} className="btn btn-secondary text-sm">
                Apply
              </button>
              {promoValidation && (
                <span className={`text-sm ${promoValidation.valid ? 'text-green-500' : 'text-red-500'}`}>
                  {promoValidation.valid ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan) => {
              const monthlyPrice = plan.price_monthly
              const annualPrice = plan.price_annual
              const monthlySavings = Math.round(((monthlyPrice * 12 - annualPrice) / (monthlyPrice * 12)) * 100)
              const features = PLAN_FEATURES[plan.id] || plan.features

              return (
                <div
                  key={plan.id}
                  className="p-5 border border-dark-border rounded-lg hover:border-gray-600 transition-all"
                >
                  <h3 className="font-semibold text-xl text-center mb-4">{plan.name}</h3>

                  {/* Features */}
                  <ul className="space-y-2 mb-6">
                    {features.slice(0, 5).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* Monthly Option */}
                  <div className="border-t border-dark-border pt-4 mb-4">
                    <div className="flex items-baseline justify-between mb-2">
                      <span className="text-gray-400 text-sm">Monthly</span>
                      <div>
                        <span className="text-2xl font-bold">${(monthlyPrice / 100).toLocaleString()}</span>
                        <span className="text-gray-400 text-sm">/mo</span>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setBillingCycle('monthly')
                        handleCheckout(plan.id)
                      }}
                      disabled={isCheckingOut || (subscription?.plan_id === plan.id && subscription?.billing_cycle === 'monthly')}
                      className="btn btn-secondary w-full text-sm"
                    >
                      {isCheckingOut && billingCycle === 'monthly' ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
                      ) : subscription?.plan_id === plan.id && subscription?.billing_cycle === 'monthly' ? (
                        'Current Plan'
                      ) : subscription ? (
                        'Switch to Monthly'
                      ) : (
                        'Pay Monthly'
                      )}
                    </button>
                  </div>

                  {/* Annual Option */}
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-green-400 text-sm font-medium">Annual</span>
                      <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                        Save {monthlySavings}%
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between mb-2">
                      <span className="text-gray-400 text-xs">${Math.round(annualPrice / 12 / 100).toLocaleString()}/mo</span>
                      <div>
                        <span className="text-2xl font-bold">${(annualPrice / 100).toLocaleString()}</span>
                        <span className="text-gray-400 text-sm">/yr</span>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setBillingCycle('annual')
                        handleCheckout(plan.id)
                      }}
                      disabled={isCheckingOut || (subscription?.plan_id === plan.id && subscription?.billing_cycle === 'annual')}
                      className="btn btn-primary w-full text-sm"
                    >
                      {isCheckingOut && billingCycle === 'annual' ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
                      ) : subscription?.plan_id === plan.id && subscription?.billing_cycle === 'annual' ? (
                        'Current Plan'
                      ) : subscription ? (
                        'Switch to Yearly'
                      ) : (
                        'Pay Yearly'
                      )}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Payment Methods */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Payment Methods</h2>
          <button
            onClick={() => setShowAddCard(true)}
            className="btn btn-secondary text-sm"
            disabled={!stripeLoaded}
          >
            <Plus className="w-4 h-4" />
            Add Card
          </button>
        </div>

        {/* Add Card Modal */}
        {showAddCard && stripeInstance && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="card w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Add Payment Method</h3>
                <button onClick={() => setShowAddCard(false)} className="text-gray-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <Elements stripe={stripeInstance}>
                <AddCardForm
                  onSuccess={() => {
                    setShowAddCard(false)
                    loadBillingData()
                  }}
                  onCancel={() => setShowAddCard(false)}
                />
              </Elements>
            </div>
          </div>
        )}

        {paymentMethods.length > 0 ? (
          <div className="space-y-3">
            {paymentMethods.map((pm) => (
              <div key={pm.id} className="flex items-center justify-between p-4 bg-gray-800 rounded-lg">
                <div className="flex items-center gap-4">
                  <CreditCard className="w-8 h-8 text-gray-400" />
                  <div>
                    <p className="font-medium">
                      {pm.card_brand.charAt(0).toUpperCase() + pm.card_brand.slice(1)} ****{pm.card_last4}
                    </p>
                    <p className="text-sm text-gray-400">Expires {pm.card_exp_month}/{pm.card_exp_year}</p>
                  </div>
                  {pm.is_default && <span className="badge badge-info">Default</span>}
                </div>
                <div className="flex items-center gap-2">
                  {!pm.is_default && (
                    <button
                      onClick={() => handleSetDefaultPaymentMethod(pm.id)}
                      className="text-sm text-primary-500 hover:underline"
                    >
                      Set Default
                    </button>
                  )}
                  <button
                    onClick={() => handleDeletePaymentMethod(pm.id)}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-4">No payment methods on file</p>
        )}
      </div>

      {/* Invoices */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Invoice History</h2>
          <button
            onClick={handleSyncInvoices}
            disabled={isSyncingInvoices}
            className="btn btn-secondary text-sm"
            title="Sync invoices from Stripe"
          >
            <RefreshCw className={`w-4 h-4 ${isSyncingInvoices ? 'animate-spin' : ''}`} />
            {isSyncingInvoices ? 'Syncing...' : 'Sync'}
          </button>
        </div>
        {invoices.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Period</th>
                <th>Amount</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id}>
                  <td>{new Date(inv.created_at).toLocaleDateString()}</td>
                  <td>
                    {new Date(inv.period_start).toLocaleDateString()} - {new Date(inv.period_end).toLocaleDateString()}
                  </td>
                  <td>${(inv.amount_paid / 100).toFixed(2)} {inv.currency.toUpperCase()}</td>
                  <td>
                    <span className={`badge ${inv.status === 'paid' ? 'badge-success' : 'badge-warning'}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td>
                    {inv.invoice_pdf_url && (
                      <a href={inv.invoice_pdf_url} target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:underline inline-flex items-center gap-1">
                        <Download className="w-4 h-4" />
                        PDF
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-gray-400 text-center py-4">No invoices yet</p>
        )}
      </div>
    </div>
  )
}
